import { ElasticVariant, ResultCardProductProps, useProductPrice } from "@usereactify/search";
import React, { memo, useContext, useEffect, useMemo, useState } from "react";
import { ViewSettingsContext } from "../../context/ViewSettingsContext";
import { ResultsItemTag } from "./ResultsItemTag";
import { ResultsSwatchesWithShopifyVariant } from "./ResultsSwatchesWithShopifyVariant";

export type ResultsItemProps = React.ComponentProps<NonNullable<ResultCardProductProps["render"]>>;

export const BaseResultsItem: React.FC<ResultsItemProps> = (props) => {
  const { product, itemRef, handleClick } = props;
  const context = useContext(ViewSettingsContext);
  if (context === null) {
    throw new Error("ViewSettingsContext must be used within a ViewSettingsProvider");
  }
  const { isMainImage, isSecondaryImage } = context;
  const { image, handle, title, tags, variant_options } = product;
  const { formattedPrice, formattedCompareAtPrice, onSale, price, compareAtPrice } = useProductPrice(product);

  const [currentVariant, setCurrentVariant] = useState(product.variants?.[0]);
  const handleVariantChangeBasedOnColorOption = (variant: ElasticVariant) => {
    setCurrentVariant(variant);
  };

  // check if tags have 'range:' as prefix on it:
  const range = tags?.find((tag) => tag.includes("range:"));
  const rangeUrl = range?.replace("range:", "").replace(/\s/g, "-").toLowerCase();
  const pageUrl = window.location.pathname;
  const pageUrlResult = pageUrl.replace(/^(\/collections\/[^\/]+)\/.*$/, "$1");

  // getting all products with group:collection-handle
  // const group = tags?.find((t: string | string[]) => !t.includes("colour-group:") && t.includes("group:"));

  const getSecondaryImage = () => {
    if (product.images && product.images.length > 1) return product.images[1].src;
    // fallback to main image
    return image || "";
  };
  // variant featured image
  const [swatchImage, setSwatchImage] = useState<string | null | undefined>(null);

  const [newTag, comingSoon, onlineExclusive, preorder, swatchGroup] = useMemo(() => {
    const tagLabels = [false, false, false, false];
    tags?.forEach((tag) => {
      if (tag.includes("New") || tag.includes("new")) tagLabels[0] = true;
      if (tag === "coming-soon") tagLabels[1] = true;
      if (tag === "onlineexclusive") tagLabels[2] = true;
      if (tag === "preorder") tagLabels[3] = true;
      if (tag.includes("group:") && !tag.includes("colour-group:")) tagLabels[4] = true;
    });
    return tagLabels;
  }, [tags]);

  // Check for bundle price:
  const [bundlePrice, setBundlePrice] = useState<string | null | undefined>(null);
  const [bundleCompareAtPrice, setBundleCompareAtPrice] = useState<string | null | undefined>(null);
  const hasBundleTag = tags?.includes("bundle"); // remember to change "js-product-bundle-price-widget.liquid"
  // fetch bundle price:
  useEffect(() => {
    if (!hasBundleTag) return;
    fetch(`https://www.remarkablefurniture.com.au/products/${handle}?view=bundle-price`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.text();
      })
      .then((data) => {
        const prices = data.split(",");
        if (prices.length === 2) {
          const price = prices[0].trim(); // Total price
          const compareAtPrice = prices[1].trim(); // Compare-at price

          // Set the extracted prices to your state or variables
          setBundlePrice(price);
          setBundleCompareAtPrice(compareAtPrice);
        } else {
          console.error("Error: Unable to extract both prices.");
        }
      })
      .catch((error) => {
        console.error("Error during fetch or data processing:", error);
      });
  }, [handle, hasBundleTag]);

  const soldOut = useMemo(() => {
    return product.variants?.every((variant: { available: boolean }) => !variant?.available);
  }, [product.variants]);

  const isOnSale = useMemo(() => {
    return !!(currentVariant?.compare_at_price && currentVariant?.compare_at_price > currentVariant?.price);
  }, [currentVariant]);

  const badges = useMemo(
    () => (
      <>
        <div className="grid-product__tags">
          {isOnSale && <div className="grid-product__tag grid-product__tag--sale">Sale</div>}
          {/* {onSale || <div className="grid-product__tag grid-product__tag--sale">Sale</div>} */}
          {newTag && <div className="grid-product__tag grid-product__tag--custom">New</div>}
          {comingSoon && <div className="grid-product__tag grid-product__tag--custom">Coming Soon</div>}
          {onlineExclusive && <div className="grid-product__tag grid-product__tag--custom">Online Exclusive</div>}
          {soldOut && !swatchGroup && !preorder && (
            <div className="grid-product__tag grid-product__tag--sold-out">PRE-ORDER</div>
          )}
        </div>
      </>
    ),
    [title, formattedPrice, onSale, variant_options, tags]
  );

  const noMarginStyle: React.CSSProperties = {
    margin: 0,
  };
  const noPbStyle: React.CSSProperties = {
    paddingBottom: 0,
  };
  const flexCol16pxGapStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  };

  return (
    <div
      ref={itemRef}
      onClick={handleClick}
      className="grid-item grid-product product-grid-item"
      data-product-handle={product.handle}
      data-product-id={product.id}
    >
      <div className="grid-item__content">
        <div className="grid-product__image-wrap">
          <a
            href={
              pageUrl.includes("search") ? `/products/${product.handle}` : `${pageUrlResult}/products/${product.handle}`
            }
            title={product.title}
          >
            {badges}
            {swatchImage ? (
              <div className="grid__image-ratio grid__image-ratio--square">
                <img className="lazyautosizes lazyloaded" loading="lazy" decoding="async" src={swatchImage} />
              </div>
            ) : (
              <>
                <div className="grid__image-ratio grid__image-ratio--square">
                  <img
                    className="lazyautosizes lazyloaded"
                    loading="lazy"
                    decoding="async"
                    src={(isMainImage ? image : getSecondaryImage()) || ""}
                  />
                </div>
                <div className="grid-product__secondary-image small--hide">
                  <img
                    className="lazyautosizes lazyloaded"
                    loading="lazy"
                    decoding="async"
                    src={(isMainImage ? getSecondaryImage() : image) || ""}
                  />
                </div>
              </>
            )}
          </a>
        </div>

        <div className="grid-item__meta grid-item__meta--custom grid-item__meta--custom-flex">
          <ResultsItemTag product={product} />
          <a
            href={
              pageUrl.includes("search") ? `/products/${product.handle}` : `${pageUrlResult}/products/${product.handle}`
            }
            title={product.title}
            className="grid-item__link"
          >
            <h5 className="grid-product__title grid-product__title-custom" style={noMarginStyle}>
              {product.title}
            </h5>
          </a>
          <div className="grid-item__meta--custom-flex">
            <div className="grid-product__price grid-product__price-custom">
              {bundlePrice ? (
                <>
                  <span className="grid-product__price--current">${bundlePrice} </span>
                  {bundleCompareAtPrice && bundlePrice !== bundleCompareAtPrice && (
                    <>
                      <span className="grid-product__price--original">${bundleCompareAtPrice}</span>
                    </>
                  )}
                </>
              ) : (
                <>
                  <span className="grid-product__price--current">
                    {(currentVariant?.price ?? price?.value ?? 0).toFixed(2)}{" "}
                  </span>
                  {isOnSale && currentVariant && currentVariant.compare_at_price && (
                    <>
                      <span className="grid-product__price--original">
                        {" "}
                        ${currentVariant.compare_at_price.toFixed(2)}
                      </span>
                      <span className="grid-product__price--savings">
                        Save ${(currentVariant.compare_at_price - currentVariant.price).toFixed(2)}
                      </span>
                    </>
                  )}
                </>
              )}
            </div>
            {range && (
              <a href={`/collections/${rangeUrl}`} title={range} className="grid-item__link">
                <p className="product-link grey-dark view-this-range-custom" style={noPbStyle}>
                  View this range
                </p>
              </a>
            )}
            <ResultsSwatchesWithShopifyVariant
              product={product}
              tags={tags}
              handleVariantChangeBasedOnColorOption={handleVariantChangeBasedOnColorOption}
              setSwatchImage={setSwatchImage}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const ResultsItem = memo(BaseResultsItem, (prevProps, nextProps) => {
  return (
    prevProps.product.id === nextProps.product.id &&
    prevProps.product.title === nextProps.product.title &&
    prevProps.product.handle === nextProps.product.handle &&
    prevProps.product.variants?.[0]?.price === nextProps.product.variants?.[0]?.price &&
    prevProps.product.image === nextProps.product.image &&
    JSON.stringify(prevProps.product.tags) === JSON.stringify(nextProps.product.tags)
  );
});
